import React, { FC, useEffect, useMemo } from 'react';
import { useAction, useAtom } from '@reatom/npm-react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import {
  useServices,
  DefaultHeader,
  PuzzleCategory,
  Routes,
  GameLeaveTool,
  openLeaveGameModalAction,
  t,
  puzzlesAPIAtom,
  PuzzleModeProgress,
} from '@whitesharx/starfall-frontend-core';

import {
  PuzzleMode,
  SolvedPuzzles,
  LeaveButton,
  COINS_REWARD_IN_SERIES,
  PUZZLE_COUNT_IN_SERIES,
} from '@whitesharx/starfall-frontend-core/challenges_screen';
import '@whitesharx/starfall-frontend-core/challenges_screen.css';

import UserInfo from '~/src/components/UserInfo';
import ChessRoyaleBanner from '~/src/components/ChessRoyaleBanner';

import { AppComponentType, AppTypes } from '../_app';
import { parseQueryParams } from '~/src/utils/parseQueryParams';
import { bannerContainerStyles } from '~/styles/pages/common.css';
import { solvedPuzzlesContainer } from '~/styles/pages/challenges.css';
import CommonCustomApp from '~/src/components/CustomApp/CommonCustomApp';
import PuzzlesSkeletonPage from '~/src/skeletons/Puzzles';
import { updateRightContainerAction } from '~/src/models/layout';

const PuzzleRealPage: FC = () => {
  const { useResponsive, Adsense } = useServices();
  const updateRightContainer = useAction(updateRightContainerAction);
  const { isDesktop } = useResponsive();

  const openLeaveModal = useAction(openLeaveGameModalAction);
  const [{ progress }] = useAtom(puzzlesAPIAtom);

  const router = useRouter();
  const category = router.query.category || parseQueryParams(router.asPath, 'category');
  const goToChallenges = () => router.push(Routes.challenges);

  const rightContainer = useMemo(() => {
    if (isDesktop) {
      return (
        <>
          <UserInfo withoutProfileLink />
          <div className={solvedPuzzlesContainer}>
            <SolvedPuzzles
              key="puzzles"
              solved={progress?.solvedCount || 0}
              total={(progress as PuzzleModeProgress)?.totalCount || PUZZLE_COUNT_IN_SERIES}
              coins={COINS_REWARD_IN_SERIES}
            />
            <LeaveButton onLeave={openLeaveModal} />
          </div>
          {Adsense.renderBanner({
            placement: 'free_mode_puzzles_screen_banner_right',
            format: 'rectangle',
            slot: 'bannerRight',
          })}
          <div className={bannerContainerStyles}>
            <ChessRoyaleBanner />
          </div>
        </>
      );
    }
  }, [isDesktop, progress?.solvedCount, (progress as PuzzleModeProgress)?.totalCount]);

  const topContainer = useMemo(() => {
    if (!isDesktop) {
      return (
        <SolvedPuzzles
          key="puzzles"
          solved={progress?.solvedCount || 0}
          total={(progress as PuzzleModeProgress)?.totalCount || PUZZLE_COUNT_IN_SERIES}
          coins={COINS_REWARD_IN_SERIES}
        />
      );
    }
  }, [isDesktop, progress?.solvedCount, (progress as PuzzleModeProgress)?.totalCount]);

  useEffect(() => {
    updateRightContainer(rightContainer);
  }, [rightContainer]);

  return (
    <>
      <Head>
        <title>{t('browser-title.challenges')}</title>
      </Head>
      <>
        {!isDesktop && <DefaultHeader leftSlot={<GameLeaveTool key="puzzle-leave-tool" onClick={openLeaveModal} />} />}
        {category && (
          <PuzzleMode category={category as PuzzleCategory} onLeave={goToChallenges} topContainer={topContainer} />
        )}
      </>
    </>
  );
};

const PuzzlesPage: FC<AppTypes> & AppComponentType = (props) =>
  props.skeleton ? <PuzzlesSkeletonPage /> : <PuzzleRealPage {...props} />;

PuzzlesPage.render = (props) => <CommonCustomApp {...props} />;

export default PuzzlesPage;
